import DOMPurify from 'dompurify';

export interface RichPromptContent {
  html: string;
  plainText: string;
  rawText: string;
}

const ALLOWED_TAGS = [
  'blockquote',
  'br',
  'del',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'hr',
  'li',
  'ol',
  'p',
  's',
  'strong',
  'u',
  'ul',
];

const PLACEHOLDERS: Array<[RegExp, string]> = [
  [/<\s*u\s*>/gi, '%%PROMPT_U_OPEN%%'],
  [/<\s*\/\s*u\s*>/gi, '%%PROMPT_U_CLOSE%%'],
  [/<\s*(del|s)\s*>/gi, '%%PROMPT_DEL_OPEN%%'],
  [/<\s*\/\s*(del|s)\s*>/gi, '%%PROMPT_DEL_CLOSE%%'],
  [/<\s*(strong|b)\s*>/gi, '%%PROMPT_STRONG_OPEN%%'],
  [/<\s*\/\s*(strong|b)\s*>/gi, '%%PROMPT_STRONG_CLOSE%%'],
  [/<\s*(em|i)\s*>/gi, '%%PROMPT_EM_OPEN%%'],
  [/<\s*\/\s*(em|i)\s*>/gi, '%%PROMPT_EM_CLOSE%%'],
];

const PLACEHOLDER_TAGS: Record<string, string> = {
  '%%PROMPT_U_OPEN%%': '<u>',
  '%%PROMPT_U_CLOSE%%': '</u>',
  '%%PROMPT_DEL_OPEN%%': '<del>',
  '%%PROMPT_DEL_CLOSE%%': '</del>',
  '%%PROMPT_STRONG_OPEN%%': '<strong>',
  '%%PROMPT_STRONG_CLOSE%%': '</strong>',
  '%%PROMPT_EM_OPEN%%': '<em>',
  '%%PROMPT_EM_CLOSE%%': '</em>',
};

const CHANGE_INSERT_STYLE = 'color: #8B1E1E;';

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function preserveAllowedInlineTags(value: string): string {
  return PLACEHOLDERS.reduce((next, [pattern, replacement]) => next.replace(pattern, replacement), value);
}

function restoreAllowedInlineTags(value: string): string {
  return Object.entries(PLACEHOLDER_TAGS).reduce(
    (next, [placeholder, tag]) => next.replace(new RegExp(placeholder, 'g'), tag),
    value,
  );
}

function formatInline(value: string): string {
  const preserved = preserveAllowedInlineTags(value);
  const escaped = restoreAllowedInlineTags(escapeHtml(preserved));

  return escaped
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.+?)__/g, '<strong>$1</strong>')
    .replace(/~~(.+?)~~/g, '<del>$1</del>')
    .replace(/\+\+(.+?)\+\+/g, '<u>$1</u>')
    .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');
}

function sanitizePromptHtml(html: string): string {
  const sanitized = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: [],
  });

  return sanitized.replace(
    /(<del>[\s\S]*?<\/del>)(\s*)<strong>/g,
    `$1$2<strong style="${CHANGE_INSERT_STYLE}">`,
  );
}

function closeList(parts: string[], listTag: 'ul' | 'ol' | null): null {
  if (listTag) {
    parts.push(`</${listTag}>`);
  }
  return null;
}

export function promptTextToHtml(source: string): string {
  if (!source.trim()) return '';

  const parts: string[] = [];
  const paragraphLines: string[] = [];
  let listTag: 'ul' | 'ol' | null = null;

  const flushParagraph = () => {
    if (paragraphLines.length === 0) return;
    parts.push(`<p>${paragraphLines.map(formatInline).join('<br/>')}</p>`);
    paragraphLines.length = 0;
  };

  const lines = source.replace(/\r\n/g, '\n').split('\n');

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    const trimmed = line.trim();

    if (!trimmed) {
      flushParagraph();
      listTag = closeList(parts, listTag);
      continue;
    }

    const heading = trimmed.match(/^(#{1,4})\s+(.+)$/);
    if (heading) {
      flushParagraph();
      listTag = closeList(parts, listTag);
      const level = Math.min(heading[1].length, 4);
      parts.push(`<h${level}>${formatInline(heading[2])}</h${level}>`);
      continue;
    }

    if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
      flushParagraph();
      listTag = closeList(parts, listTag);
      parts.push('<hr/>');
      continue;
    }

    const ordered = trimmed.match(/^\d+\.\s+(.+)$/);
    if (ordered) {
      flushParagraph();
      if (listTag !== 'ol') {
        listTag = closeList(parts, listTag);
        parts.push('<ol>');
        listTag = 'ol';
      }
      parts.push(`<li>${formatInline(ordered[1])}</li>`);
      continue;
    }

    const unordered = trimmed.match(/^[-•]\s+(.+)$/) || trimmed.match(/^\*\s+(.+)$/);
    if (unordered) {
      flushParagraph();
      if (listTag !== 'ul') {
        listTag = closeList(parts, listTag);
        parts.push('<ul>');
        listTag = 'ul';
      }
      parts.push(`<li>${formatInline(unordered[1])}</li>`);
      continue;
    }

    listTag = closeList(parts, listTag);
    paragraphLines.push(line);
  }

  flushParagraph();
  closeList(parts, listTag);

  return sanitizePromptHtml(parts.join(''));
}

export function htmlToPlainText(html: string): string {
  const withBreaks = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<hr\s*\/?>/gi, '\n---\n')
    .replace(/<li>/gi, '- ')
    .replace(/<\/(p|h[1-4]|li|blockquote)>/gi, '\n')
    .replace(/<\/(ul|ol)>/gi, '\n');

  if (typeof document !== 'undefined') {
    const el = document.createElement('div');
    el.innerHTML = withBreaks;
    return (el.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
  }

  return withBreaks
    .replace(/<[^>]+>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function promptTextToPlainText(source: string): string {
  return htmlToPlainText(promptTextToHtml(source));
}

export function promptTextToRichContent(source: string): RichPromptContent {
  const html = promptTextToHtml(source);
  return {
    html,
    plainText: htmlToPlainText(html),
    rawText: source,
  };
}

export function promptTextsToRichContent(texts: string[]): RichPromptContent {
  if (texts.length === 0) {
    return { html: '', plainText: '', rawText: '' };
  }

  if (texts.length === 1) {
    return promptTextToRichContent(texts[0]);
  }

  const html = sanitizePromptHtml(texts
    .map((text, index) => `<h2>Variant ${index + 1}</h2>${promptTextToHtml(text)}`)
    .join('<hr/>'));

  return {
    html,
    plainText: htmlToPlainText(html),
    rawText: texts.join('\n\n---\n\n'),
  };
}
